/**
 * StableTrust IPFS helpers (aligned with @fairblock/stabletrust 2.0.11+).
 *
 * Uploads go to the self-hosted StableTrust/Kubo endpoint rather than Pinata.
 * Configure via CRA env vars (REACT_APP_ prefix required for browser bundles):
 *   REACT_APP_STABLETRUST_IPFS_UPLOAD_URL
 *   REACT_APP_STABLETRUST_IPFS_API_KEY (optional)
 *   REACT_APP_STABLETRUST_IPFS_GATEWAY_URL (optional; for fetching proofs)
 */

const UPLOAD_URL =
  process.env.REACT_APP_STABLETRUST_IPFS_UPLOAD_URL ||
  process.env.STABLETRUST_IPFS_UPLOAD_URL ||
  "";

const API_KEY =
  process.env.REACT_APP_STABLETRUST_IPFS_API_KEY ||
  process.env.STABLETRUST_IPFS_API_KEY ||
  "";

const DEFAULT_IPFS_GATEWAY = "https://ipfs.io/ipfs/";

/**
 * HTTP gateway URL for a CID.
 * Set `REACT_APP_STABLETRUST_IPFS_GATEWAY_URL` (or `REACT_APP_IPFS_GATEWAY`) to
 * match your StableTrust IPFS read gateway.
 *
 * @param {string} cid
 * @returns {string}
 */
export function ipfsGatewayUrlForCid(cid) {
  let gw = (
    process.env.REACT_APP_STABLETRUST_IPFS_GATEWAY_URL ||
    process.env.STABLETRUST_IPFS_GATEWAY_URL ||
    process.env.REACT_APP_IPFS_GATEWAY ||
    DEFAULT_IPFS_GATEWAY
  ).trim();
  if (!gw) {
    gw = DEFAULT_IPFS_GATEWAY;
  }
  // Accept either a bare origin or a gateway path that already ends in /ipfs/
  if (!/\/ipfs\/?$/i.test(gw)) {
    if (!gw.endsWith("/")) gw += "/";
    gw += "ipfs/";
  } else if (!gw.endsWith("/")) {
    gw += "/";
  }
  return gw + String(cid).replace(/^\//, "").replace(/^ipfs:\/\//i, "");
}

function resolveUploadOptions(options = {}) {
  const uploadUrl = String(
    options.uploadUrl || options.ipfsUploadUrl || UPLOAD_URL || "",
  ).trim();
  if (!uploadUrl) {
    throw new Error(
      "StableTrust IPFS upload URL is not set. Set REACT_APP_STABLETRUST_IPFS_UPLOAD_URL",
    );
  }
  return {
    uploadUrl,
    apiKey:
      options.apiKey ||
      options.ipfsApiKey ||
      API_KEY ||
      null,
  };
}

/**
 * Upload a JSON-serializable object to StableTrust IPFS and return its CID.
 * @param {any} data
 * @param {object} [options]
 * @returns {Promise<string>} Bare CID string
 */
export async function uploadJsonToIpfs(data, options = {}) {
  const json = JSON.stringify(data);
  return uploadBytesToIpfs(new TextEncoder().encode(json), "proof.json", options);
}

/**
 * Upload raw proof bytes to StableTrust IPFS and return the bare CID.
 * Protocol matches @fairblock/stabletrust `uploadBytesToIpfs`:
 * POST raw bytes, Content-Type application/octet-stream, optional Bearer auth.
 *
 * @param {Uint8Array|ArrayBuffer|Array<number>} bytes
 * @param {string} [name='proof.bin']
 * @param {object} [options]
 * @returns {Promise<string>} Bare CID string
 */
export async function uploadBytesToIpfs(bytes, name = "proof.bin", options = {}) {
  const { uploadUrl, apiKey } = resolveUploadOptions(options);
  const uint8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  if (uint8.length === 0) {
    throw new Error("cannot upload an empty proof to IPFS");
  }

  const headers = {
    "Content-Type": "application/octet-stream",
  };
  if (name) {
    headers["X-StableTrust-Filename"] = String(name);
  }
  if (apiKey) {
    headers.Authorization = `Bearer ${apiKey}`;
  }

  const res = await fetch(uploadUrl, {
    method: "POST",
    headers,
    body: uint8,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`StableTrust IPFS upload failed: ${text}`);
  }

  const contentType = res.headers.get("content-type") || "";
  let cid;
  if (contentType.includes("application/json")) {
    const jsonRes = await res.json();
    cid =
      jsonRes?.cid ??
      jsonRes?.CID ??
      jsonRes?.Cid ??
      jsonRes?.Hash ??
      jsonRes?.hash;
  } else {
    cid = await res.text();
  }

  cid = String(cid || "")
    .trim()
    .replace(/^ipfs:\/\//i, "");
  if (!cid) {
    throw new Error("StableTrust IPFS upload returned no CID");
  }
  return cid;
}

/**
 * Whether a UTF-8 decoded proof arg looks like an IPFS CID / pointer.
 * Supports legacy `ipfs://<cid>` and current bare-CID (SDK 2.0.11+) encodings.
 * @param {string} text
 * @returns {string|null} Bare CID or null
 */
export function extractIpfsCidFromProofText(text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return null;
  if (/^ipfs:\/\//i.test(trimmed)) {
    const cid = trimmed.replace(/^ipfs:\/\//i, "").trim();
    return cid || null;
  }
  // CIDv0 (Qm…) or CIDv1 (bafy… / bafk… / etc.)
  if (/^(Qm[1-9A-HJ-NP-Za-km-z]{44}|baf[a-z0-9]{20,})$/i.test(trimmed)) {
    return trimmed;
  }
  return null;
}
